import type { ISigner } from "applesauce-signers";
import { nip98 } from "nostr-tools";

export interface GitHttpAuthorizationProvider {
  readonly accessScope: string;
  getAuthorization(repoUrl: string): Promise<string>;
}

interface CachedCredential {
  authorization: string;
  expiresAt: number;
}

// GRASP-08 accepts credentials only while the signed event is within 60
// seconds of the server clock. Keep a small margin for transit and clock skew.
const CREDENTIAL_EXPIRY_MARGIN_MS = 5_000;

export function canonicalGitRepositoryUrl(repoUrl: string): string {
  const url = new URL(repoUrl);
  url.search = "";
  url.hash = "";
  url.pathname = url.pathname
    .replace(/\/(?:info\/refs|git-upload-pack|git-receive-pack)\/?$/, "")
    .replace(/\/+$/, "");
  return url.toString().replace(/\/$/, "");
}

export function createGitHttpAuthorizationProvider(
  pubkey: string,
  signer: ISigner,
): GitHttpAuthorizationProvider {
  const cache = new Map<string, CachedCredential>();
  const inFlight = new Map<string, Promise<string>>();

  return {
    accessScope: `private:${pubkey}`,
    async getAuthorization(repoUrl: string): Promise<string> {
      const canonicalUrl = canonicalGitRepositoryUrl(repoUrl);
      const cached = cache.get(canonicalUrl);
      if (cached && cached.expiresAt > Date.now()) {
        return cached.authorization;
      }

      const pending = inFlight.get(canonicalUrl);
      if (pending) return pending;

      let signedCreatedAt: number | undefined;
      const credential = nip98
        .getToken(
          canonicalUrl,
          "GET",
          async (template) => {
            const event = await signer.signEvent(template);
            signedCreatedAt = event.created_at;
            return event;
          },
          true,
        )
        .then((authorization) => {
          // Derive expiry from the event the signer actually returned, rather
          // than from completion time. A remote signer may take long enough
          // that a fixed post-signing TTL outlives the server's ±60s window.
          if (signedCreatedAt !== undefined) {
            const expiresAt =
              (signedCreatedAt + 60) * 1_000 - CREDENTIAL_EXPIRY_MARGIN_MS;
            if (expiresAt > Date.now()) {
              cache.set(canonicalUrl, { authorization, expiresAt });
            }
          }
          return authorization;
        })
        .finally(() => {
          inFlight.delete(canonicalUrl);
        });

      inFlight.set(canonicalUrl, credential);
      return credential;
    },
  };
}

export async function gitAuthorizationHeaders(
  provider: GitHttpAuthorizationProvider | undefined,
  repoUrl: string,
): Promise<Record<string, string> | undefined> {
  if (!provider) return undefined;
  return { Authorization: await provider.getAuthorization(repoUrl) };
}
